import React, { useState, useRef, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Document, Page } from 'react-pdf';
import { Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import uuid from 'react-uuid';
import { Tag } from './AddDocsInterface';

type TagItem = {
    value: string;
    description: string;
};

// Extended Tag type with width
type ExtendedTag = Tag & {
    width?: number;
};

type Props = {
    file: File;
    show: boolean;
    handleClose: () => void;
    onFinished: (file: File) => void;
    tagList: any;
    setTags: any;
    tags: ExtendedTag[];
    method: any;
    buttonLoading: any;
    setButtonLoading: any
    SetUp: string
}

const PdfTagEditor = ({ file, show, handleClose, onFinished, tagList, setTags, tags, method, buttonLoading, setButtonLoading, SetUp }: Props) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [selectedTagType, setSelectedTagType] = useState(tagList?.getAllTags?.tags?.CustomerName?.value || '');
    const [isLoading, setIsLoading] = useState(false);
    const [cleanPdfFile, setCleanPdfFile] = useState<File | null>(null);
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
    const containerRefs = useRef<any>([]);
    const [pageDimensions, setPageDimensions] = useState<{ width: number, height: number } | null>(null);
    // --- CONSTANTS ---
    const PDF_FONT_SIZE = Number(SetUp) || 10;
    const PDF_BOX_HEIGHT = PDF_FONT_SIZE * 1.6;
    const PREVIEW_WIDTH = 750;
    const DEFAULT_WIDTH_PERCENTAGE = 15;


    const extractTagsAndCleanPdf = async (pdfFile: File) => {
        try {
            setIsLoading(true);
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const form = pdfDoc.getForm();
            const pages = pdfDoc.getPages();

            const firstPage = pages[0];
            const dimensions = firstPage.getSize();
            setPageDimensions(dimensions);

            const extractedTags: ExtendedTag[] = [];
            const fields = form.getFields();

            fields.forEach((field: any) => {
                const fieldName = field.getName();

                // Match pattern: {label},page,xXX.XX,yYY.YY,wWW.WW,uuid
                const standardMatch = fieldName.match(/^\{([^}]+)\},(\d+),/);
                const coordMatch = fieldName.match(/,x([\d.]+),y([\d.]+)(?:,w([\d.]+))?,/);

                if (!standardMatch) return;

                const labelPart = `{${standardMatch[1]}}`;
                const pageNum = parseInt(standardMatch[2], 10);

                if (isNaN(pageNum) || pageNum >= pages.length) return;

                let xPercent = 0;
                let yPercent = 0;
                let widthPercent = DEFAULT_WIDTH_PERCENTAGE;

                if (coordMatch) {
                    xPercent = parseFloat(coordMatch[1]);
                    yPercent = parseFloat(coordMatch[2]);
                    if (coordMatch[3]) {
                        widthPercent = parseFloat(coordMatch[3]);
                    }
                } else {
                    const page = pages[pageNum];
                    const { width, height } = page.getSize();
                    const widgets = field.acroField.getWidgets();
                    if (widgets.length) {
                        const rect = widgets[0].getRectangle();
                        xPercent = (rect.x / width) * 100;
                        // Convert from PDF Y (bottom) to HTML Y (top)
                        yPercent = ((height - rect.y - rect.height) / height) * 100;
                        widthPercent = (rect.width / width) * 100;
                    }
                }
                extractedTags.push({
                    id: Date.now() + extractedTags.length,
                    x: xPercent,
                    y: yPercent,
                    page: pageNum,
                    label: labelPart,
                    width: widthPercent,
                });
            });

            // Remove existing fields
            const fieldsToRemove = form.getFields();
            fieldsToRemove.forEach((field: any) => {
                try { form.removeField(field); } catch (e) { console.error(e); }
            });

            const cleanPdfBytes = await pdfDoc.save();
            const cleanFile = new File([cleanPdfBytes as any], pdfFile.name, { type: 'application/pdf' });
            setCleanPdfFile(cleanFile);
            if (extractedTags.length) setTags(extractedTags);
        } catch (error) {
            console.error('Error:', error);
            setCleanPdfFile(pdfFile);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setButtonLoading(false);
        setSelectedTagId(null);
        if (!file || !show) return;
        if (tags.length >= 0) {
            extractTagsAndCleanPdf(file);
        } else {
            setCleanPdfFile(file);
        }
    }, [file, show]);

    useEffect(() => {
        if (tagList?.getAllTags?.tags?.CustomerName?.value) {
            setSelectedTagType(tagList.getAllTags.tags.CustomerName.value);
        }
    }, [tagList]);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        containerRefs.current = new Array(numPages);
    };

    const getScaleFactor = () => {
        if (!pageDimensions) return 1;
        return PREVIEW_WIDTH / pageDimensions.width;
    };

    const scale = getScaleFactor();
    const SCALED_FONT_SIZE = PDF_FONT_SIZE * scale;
    const SCALED_BOX_HEIGHT = PDF_BOX_HEIGHT * scale;

    const handlePageClick = (e: React.MouseEvent, pageIdx: number) => {
        if ((e.target as HTMLElement).closest('.tag-instance')) return;

        const rect = e.currentTarget.getBoundingClientRect();

        const clickXPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const clickYPercent = ((e.clientY - rect.top) / rect.height) * 100;

        // Convert box height to percentage
        const boxHeightPercent = (SCALED_BOX_HEIGHT / rect.height) * 100;

        const newTag: ExtendedTag = {
            id: Date.now(),
            x: clickXPercent,
            y: clickYPercent - (boxHeightPercent / 2), // CENTER vertically
            page: pageIdx,
            label: selectedTagType,
            width: DEFAULT_WIDTH_PERCENTAGE
        };

        setTags([...tags, newTag]);
        setSelectedTagId(newTag.id);
    };


    const handleDragEnd = (id: number, info: any, pageIdx: number) => {
        const container = containerRefs.current[pageIdx];
        if (!container) return;
        const rect = container.getBoundingClientRect();

        setTags((prevTags: ExtendedTag[]) => prevTags.map((tag: ExtendedTag) => {
            if (tag.id === id) {
                // Get the current DOM position (after drag)
                const element = document.querySelector(`[data-tag-id="${id}"]`) as HTMLElement;
                if (!element) return tag;

                const elementRect = element.getBoundingClientRect();

                // Calculate new position as percentage relative to container
                const newX = ((elementRect.left - rect.left) / rect.width) * 100;
                const newY = ((elementRect.top - rect.top) / rect.height) * 100;

                const tagWidth = tag.width || DEFAULT_WIDTH_PERCENTAGE;
                const scaledHeightPercent = (SCALED_BOX_HEIGHT / rect.height) * 100;

                // Clamp to boundaries
                const clampedX = Math.max(0, Math.min(100 - tagWidth, newX));
                const clampedY = Math.max(0, Math.min(100 - scaledHeightPercent, newY));

                return { ...tag, x: clampedX, y: clampedY };
            }
            return tag;
        }));
    };


    const removeTag = (id: number) => {
        setTags(tags.filter(tag => tag.id !== id));
        if (selectedTagId === id) setSelectedTagId(null);
    };

    const adjustTagWidth = (delta: number) => {
        if (selectedTagId === null) return;

        setTags((prevTags: ExtendedTag[]) => prevTags.map((tag: ExtendedTag) => {
            if (tag.id === selectedTagId) {
                const currentWidth = tag.width || DEFAULT_WIDTH_PERCENTAGE;
                const newWidth = Math.max(5, Math.min(50, currentWidth + delta));
                return { ...tag, width: newWidth };
            }
            return tag;
        }));
    };

    const saveAndFlatten = async () => {
        try {
            setButtonLoading(true);
            const pdfFileToUse = cleanPdfFile || file;
            const existingPdfBytes = await pdfFileToUse.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const form = pdfDoc.getForm();
            const pages = pdfDoc.getPages();
            tags.forEach((tag) => {
                const page = pages[tag.page];
                const { width: pageWidth, height: pageHeight } = page.getSize();

                // 1. Calculate field width from percentage
                const widthPercent = tag.width || DEFAULT_WIDTH_PERCENTAGE;
                const fieldWidth = (widthPercent / 100) * pageWidth;
                const pdfX = (tag.x / 100) * pageWidth;
                const pixelsFromTop = (tag.y / 100) * pageHeight;
                const pdfY = pageHeight - pixelsFromTop - PDF_BOX_HEIGHT;
                const safeX = tag.x.toFixed(2);
                const safeY = tag.y.toFixed(2);
                const safeW = widthPercent.toFixed(2);
                const fieldName = `${tag.label},${tag.page},x${safeX},y${safeY},w${safeW},${uuid()}`;

                const textField = form.createTextField(fieldName);
                textField.setText(tag.label);

                textField.addToPage(page, {
                    x: pdfX,
                    y: pdfY,
                    width: fieldWidth,
                    height: PDF_BOX_HEIGHT,
                    borderWidth: 0,
                    borderColor: undefined, // Makes border transparent
                    backgroundColor: undefined, // Makes background transparent

                });
                textField.setFontSize(PDF_FONT_SIZE);
            });

            const pdfBytes = await pdfDoc.save();
            const finalFile = new File([pdfBytes as any], file.name, {
                type: 'application/pdf',
            });

            onFinished(finalFile);
        } catch (e) {
            console.error(e);
        }
    };

    const tagOptions = Object.entries(tagList?.getAllTags?.tags || {})
        .map(([key, tag]) => ({ key, ...(tag as TagItem) }))
        .sort((a, b) => a.value.localeCompare(b.value));

    return (
        <Modal show={show} onHide={handleClose} dialogClassName='modal-dialog modal-dialog-centered mw-950px' centered scrollable>
            <div className="modal-header bg-white border-0 pb-0">
                <h5 className="m-0 fw-bold">Position Tags</h5>
                <select
                    name="tagType"
                    title="Select tag type"
                    value={selectedTagType}
                    onChange={(e) => setSelectedTagType(e.target.value)}
                    className="form-select form-select-sm w-200px ms-auto me-5"
                >
                    {tagOptions.map(tag => (
                        <option key={tag.key} value={tag.value}>
                            {`${tag.key} - ${tag.description}`}
                        </option>
                    ))}
                </select>
            </div>
            <div className="modal-body bg-gray-100 p-10">
                {/* Width Control Panel */}
                {selectedTagId !== null && (
                    <div className="alert alert-info d-flex align-items-center justify-content-between mb-5" style={{ position: 'sticky', top: 0, zIndex: 300 }}>
                        <span className="fw-bold">Selected Tag Width Control:</span>
                        <div className="btn-group">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => adjustTagWidth(-2)}
                                title="Decrease width"
                            >
                                ← Narrower
                            </button>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => adjustTagWidth(2)}
                                title="Increase width"
                            >
                                Wider →
                            </button>
                        </div>
                        <small className="text-muted ms-3">
                            Width: {tags.find(t => t.id === selectedTagId)?.width?.toFixed(1) || DEFAULT_WIDTH_PERCENTAGE}%
                        </small>
                    </div>
                )}

                {isLoading ? (
                    <div className="text-center py-20">
                        <p>Loading...</p>
                    </div>
                ) : cleanPdfFile ? (
                    <Document file={cleanPdfFile} onLoadSuccess={onDocumentLoadSuccess} loading={null}>
                        {Array.from(new Array(numPages), (el, index) => (
                            <div
                                key={`page_${index}`}
                                ref={el => containerRefs.current[index] = el}
                                className="position-relative mb-10 shadow-lg mx-auto bg-white"
                                style={{ width: 'fit-content', cursor: 'crosshair' }}
                                onClick={(e) => handlePageClick(e, index)}
                            >
                                <Page
                                    pageNumber={index + 1}
                                    width={PREVIEW_WIDTH}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                />

                                {tags.filter(t => t.page === index).map((t) => {
                                    const isSelected = selectedTagId === t.id;
                                    const tagWidth = t.width || DEFAULT_WIDTH_PERCENTAGE;

                                    return (
                                        <motion.div
                                            key={t.id}
                                            drag
                                            data-tag-id={t.id}
                                            dragMomentum={false}
                                            dragConstraints={containerRefs.current[index]}
                                            onDragEnd={(_: any, info: any) => handleDragEnd(t.id, info, index)}
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                setSelectedTagId(t.id);
                                            }}
                                            className="tag-instance position-absolute"
                                            style={{
                                                left: `${t.x}%`,
                                                top: `${t.y}%`,
                                                width: `${tagWidth}%`,
                                                height: `${SCALED_BOX_HEIGHT}px`,
                                                fontSize: `${SCALED_FONT_SIZE}px`,

                                                cursor: 'move',
                                                zIndex: isSelected ? 200 : 100,
                                                color: isSelected ? '#0066cc' : '#009ef7',
                                                fontWeight: isSelected ? '600' : '500',
                                                backgroundColor: isSelected ? 'rgba(255, 255, 200, 0.95)' : 'rgba(255, 255, 255, 0.85)',
                                                border: isSelected ? '2px solid #0066cc' : '1px solid #009ef7',
                                                boxShadow: isSelected ? '0 0 10px rgba(0, 102, 204, 0.5)' : 'none',

                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingLeft: '4px',
                                                paddingRight: '4px',

                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                userSelect: 'none',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {t.label}
                                            </span>
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeTag(t.id);
                                                }}
                                                className="opacity-50 hover-opacity-100 text-danger fw-bold"
                                                style={{
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    marginLeft: '4px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                ×
                                            </span>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        ))}
                    </Document>
                ) : null}
            </div>

            <div className="modal-footer border-0">
                <div className="d-flex align-items-center me-auto">
                    <small className="text-muted">
                        💡Tip: Data tag placement is anchored to the center-left position of the cursor. For optimal output, ensure the data tag width is properly set and that there is sufficient space in the document, especially for data tags containing longer values.
                    </small>
                </div>
                <button
                    className="btn btn-sm btn-light"
                    onClick={handleClose}
                    disabled={buttonLoading}
                >
                    Cancel
                </button>
                {method !== 'preview' && <button
                    className="btn btn-sm btn-primary px-10"
                    onClick={saveAndFlatten}
                    disabled={buttonLoading}
                >

                    {buttonLoading ? ' Saving...' : ' Save Template'}

                    {buttonLoading && (
                        <span className='spinner-border spinner-border-sm align-middle ms-2'> </span>
                    )}
                </button>}
            </div>
        </Modal>
    );
};

export default PdfTagEditor;